import axios from "axios";
import { getOrigin, getWwsBotId, getWidgetToken } from "../utils/widgetStorage";
import { getRuntimeConfig } from "./runtime-config";
import { withWidgetProtocol } from "./widgetProtocol";

// SR-6 (spec-onprem-readiness §2L): both of these used to walk `window.location.hostname`
// and END with the Intelswift production endpoints, so a widget served from a customer's
// own host silently talked to our cloud. They are runtime config now; the Intelswift host
// defaults live on in runtime-config.js so cloud is unchanged.
function getDomain() {
	return getRuntimeConfig().apiOrigin;
}

// The socket origin. Since the D11 host fold it is the SAME origin as the API (the
// `chat.intelswift.com` host that used to front it is gone), but it stays its own runtime key
// because an install may still terminate the socket somewhere else.
function getSocketUrl() {
	return getRuntimeConfig().socketUrl;
}

/**
 * socket-service dials on ONE path since the 2.3 foundation cut. A bare-root upgrade is
 * answered `unrecognized_path` **404 before the origin is read**, so a value without the path
 * is not a degraded connection — it is no connection at all.
 *
 * APPENDED idempotently rather than asserted (the shape the services settled on): a deployed
 * `SOCKET_URL` that still points at a bare root degrades to "still works", a re-pointed one is
 * not doubled, and an existing query string is preserved.
 */
const SOCKET_WS_PATH = "/socket-service/api/v1";

function socketUrl() {
	const raw = getSocketUrl() || "";
	const [ base, query ] = raw.split("?");
	const trimmed = base.replace(/\/+$/, "");
	const withPath = trimmed.endsWith(SOCKET_WS_PATH) ? trimmed : trimmed + SOCKET_WS_PATH;
	return query ? `${withPath}?${query}` : withPath;
}


class AxiosInstance {
	constructor(prefix) {
		this._axiosInstance = axios.create({ baseURL: prefix });


		this._axiosInstance.interceptors.request.use(
			function (config) {
				if (!(config.data instanceof FormData)) {
					config.headers["Content-Type"] = "application/json";
				  } else {
					delete config.headers["Content-Type"];
				  }
				const urlData = getOrigin();
				const botId = getWwsBotId();

				if (urlData && !config.headers["X-Host"] && !config.headers["X-Pathname"]) {
					config.headers["X-Host"] = urlData.hostname;
					config.headers["X-Pathname"] = urlData.pathname;
				}

				if (botId && config?.params) {
					config.params.botId = botId;
				}

				return config;
			},
			function (error) {
				return Promise.reject(error);
			}
		);

		this._axiosInstance.interceptors.response.use(
			function (response) {
				return response;
			},
			async function (error) {
				const config = error.config;

				// Only retry if config exists and not marked to skip retry
				if (!config || config.__retryCount >= 3) {
					return Promise.reject(error);
				}

				// Init retry count if missing
				config.__retryCount = config.__retryCount || 0;
				config.__retryCount += 1;

				// Simple backoff: 200ms * retryCount
				const delay = (ms) => new Promise((res) => setTimeout(res, ms));
				await delay(200 * config.__retryCount);
				// Retry the request
				return axios(config);
			}
		);
	}

	get axiosInstance() {
		return this._axiosInstance;
	}

	set axiosInstance(instance) {
		this._axiosInstance = instance;
	}
}

// NOTE: there is deliberately no workspace-service client here. The widget has never had a
// call site on one — the old `axiosWAS` on the bare `/api/v1` was dead code, and that bare
// prefix is retired estate-wide (workspace foundation, 2026-09-06).
//
// unified-knowledge-base-service — `/knowledge-service/api/v1` (canonical since 2026-09-06;
// the resource-noun `/knowledge/` prefix is retired, no alias). The widget's FAQ article +
// sub-collection reads moved off the workspace service with the docs domain.
const axiosKnowledge = new AxiosInstance(`${getDomain()}/knowledge-service/api/v1`).axiosInstance;
// unified-chat-service — `/chat-service/api/v1` (canonical since 2026-09-05; the legacy `atombot` prefix is
// retired estate-wide). Serves the two customer-facing reads the widget still makes against chat:
// `GET /conversations/all/:contactId` and `GET /contacts/wws/:contactId`.
const axiosChat = new AxiosInstance(`${getDomain()}/chat-service/api/v1`).axiosInstance;
// unified-channel-service (Phase-4 CD1, amended): the widget's TRANSPORT — the bootstrap
// `GET /widgets/bootstrap/:botId` (config + identity token; it left chat with the channel cutover;
// renamed off the retired `wws` codename by channel CD20, no alias) and
// `POST /widgets/query` (the visitor's message → `channel.inbound`). Replies still arrive over the
// socket, so nothing about the reply side moved. Mirrors axiosTickets; the edge serves
// `/channel-service/` in every shipped conf (L19).
const axiosChannels = new AxiosInstance(`${getDomain()}/channel-service/api/v1`).axiosInstance;
// unified-ticket-service (ticket extraction): the widget "my tickets" route moved off UPS.
// `/ticket-service/api/v1` since 2026-09-05 (foundation TF-D10; the old `/tickets/api/v1` is not
// served — a cached bundle answers 404 at the edge until the page reloads the loader).
const axiosTickets = new AxiosInstance(`${getDomain()}/ticket-service/api/v1`).axiosInstance;
// unified-agent-service — `/agent-service/api/v1` (canonical since 2026-09-06; `/uas/` retired,
// no alias). Serves the widget's assistant-config read at bootstrap.
const axiosAgentService = new AxiosInstance(`${getDomain()}/agent-service/api/v1`).axiosInstance;
// unified-file-service — `/file-service/api/v1` (canonical since 2026-09-06; the resource-noun
// `/files/` prefix is retired, no alias).
const axiosFiles = new AxiosInstance(`${getDomain()}/file-service/api/v1`).axiosInstance;

// Attach the widget identity token to chat-service requests — the two
// widget reads left there (/contacts/wws, /conversations/all) REQUIRE it
// (chat is token-only since 2026-10-01, pass-2 P2-G-CX14-1: no Origin fallback).
// Until the bootstrap minted one (getWidgetToken() returns null) those reads
// answer 401 — provision WIDGET_TOKEN_SECRET on channel-service (the issuer).
// Runtime 08c (D-L72): the history bootstrap also carries `x-widget-protocol: 2` — only beside a
// token, because chat records the version only for a verified widget session. spec-widget-mcp 05
// adds `x-widget-kinds` (the Tier-1 kinds this build renders) on the same request.
axiosChat.interceptors.request.use(
	function (config) {
		const widgetToken = getWidgetToken();
		if (widgetToken) {
			config.headers["Authorization"] = `Bearer ${widgetToken}`;
			withWidgetProtocol(config);
		}
		return config;
	},
	function (error) {
		return Promise.reject(error);
	}
);

// unified-channel-service verifies the same token on POST /widgets/query (token-first, the
// embed allowlist as the no-token fallback — chat's exact contract, now served there) and reads
// the PRIOR token on the bootstrap to refresh the same visitor identity instead of minting a new
// one. Same inert-until-issued behaviour as the others.
axiosChannels.interceptors.request.use(
	function (config) {
		const widgetToken = getWidgetToken();
		if (widgetToken) {
			config.headers["Authorization"] = `Bearer ${widgetToken}`;
		}
		return config;
	},
	function (error) {
		return Promise.reject(error);
	}
);

// unified-ticket-service verifies the same token on GET /tickets/chat-widget/contact/:contactId
// (ticket D1(a); renamed off the retired `wws` codename by ticket TF-30, no alias — this bundle
// and the service release together). The token is THE control there (required since the
// 2026-10-01 audit, pass 2 lane 20 — the domain-allowlist fallback and the enforce flag are gone):
// the service binds contactId/tenantId/botId to the signed identity, which is what stops a widget
// on one page from asking for another visitor's tickets; without a token the read is 401.
axiosTickets.interceptors.request.use(
	function (config) {
		const widgetToken = getWidgetToken();
		if (widgetToken) {
			config.headers["Authorization"] = `Bearer ${widgetToken}`;
		}
		return config;
	},
	function (error) {
		return Promise.reject(error);
	}
);

// unified-file-service verifies the same widget identity token (file-service B5): uploads and
// status reads 401 without it — there is no origin-allowlist fallback on that service.
axiosFiles.interceptors.request.use(
	function (config) {
		const widgetToken = getWidgetToken();
		if (widgetToken) {
			config.headers["Authorization"] = `Bearer ${widgetToken}`;
		}
		return config;
	},
	function (error) {
		return Promise.reject(error);
	}
);

export {
	axiosKnowledge,
	axiosChat,
	axiosChannels,
	axiosTickets,
	axiosAgentService, axiosFiles,
	getDomain,
	getSocketUrl,
	socketUrl,
	SOCKET_WS_PATH
};
